import React from "react";
import { useTranslation } from "react-i18next";
import type {
  CatalogShow,
  DefaultCoverLayout,
} from "@/types/catalogLayout.type";
import SeriesCover from "@/components/SeriesCover";
import {
  BadgeType,
  CounterSize,
  CoverSize,
} from "@/codegen/graphql/generated/graphql";
import { cn } from "@/lib/utils";

const coverSizes = {
  [CoverSize.XSmall]: "xs",
  [CoverSize.Small]: "sm",
  [CoverSize.Medium]: "md",
  [CoverSize.Large]: "lg",
} as const;

const counterSizes = {
  [CounterSize.XSmall]: "xs",
  [CounterSize.Small]: "sm",
  [CounterSize.Big]: "lg",
} as const;

// Exported: My List's own card and the series details episode tile (its cover
// fallback) render covers at these same sizes and must request the same CDN
// variants.
export const thumbnailHeights = {
  [CoverSize.XSmall]: { mobile: "240", desktop: "480" },
  [CoverSize.Small]: { mobile: "240", desktop: "480" },
  [CoverSize.Medium]: { mobile: "320", desktop: "540" },
  [CoverSize.Large]: { mobile: "640", desktop: "1080" },
} as const;

const badgeLabelKeys = {
  [BadgeType.New]: "catalog_badge_new_label",
  [BadgeType.OnSale]: "catalog_badge_on_sale_label",
} as const;

const DefaultCover: React.FC<{
  show: CatalogShow;
  layout: DefaultCoverLayout;
  showTitleImage?: boolean;
  priority?: boolean;
  className?: string;
  origin?: string;
}> = ({
  show,
  layout,
  showTitleImage,
  priority = false,
  className,
  origin,
}) => {
  const { t } = useTranslation();
  const badgeKey =
    layout.badge && show.badgeType !== BadgeType.None
      ? badgeLabelKeys[show.badgeType]
      : undefined;

  return (
    <SeriesCover
      className={cn("relative aspect-3/4 w-full", className)}
      size={coverSizes[layout.size]}
      thumbnailHeight={thumbnailHeights[layout.size]}
      thumbnail={show.displayThumbnail.medium}
      titleImage={
        layout.titleImage && showTitleImage
          ? show.displayTitleImage.original
          : undefined
      }
      counterSize={layout.counterSize ? counterSizes[layout.counterSize] : "sm"}
      views={layout.counterSize ? show.views : undefined}
      badge={badgeKey ? t(badgeKey) : undefined}
      seriesId={show.id}
      resumeEpisodeId={show.progression?.nextEpisode?.id}
      title={show.title}
      displayTitle={show.displayTitle}
      episodeCount={show.episodesCount}
      priority={priority}
      origin={origin}
      // A cover whose layout asks for the progression is a resume surface
      // (Continue watching): it stays on the player in both variants of the
      // SHO-758 test. Every other rail is a discovery surface and goes
      // through the series details page in the treatment.
      detailsPage={!layout.progression}
    />
  );
};

export default DefaultCover;
